// src/pages/orders/OrdersExport.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card } from "../../components/card/card.jsx";
import { fetchOrdersExport } from "../../actions/orders_actions";
import "./OrdersExport.css";


// Helper: Date -> "YYYY-MM-DD"
const toInputDate = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Default range: from 3 months ago to today
const getDefaultDates = () => {
  const today = new Date();
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  return {
    start: toInputDate(threeMonthsAgo),
    end: toInputDate(today),
  };
};


function OrdersExport() {
  const dispatch = useDispatch();

  // Show/hide product items in CSV (default: ON)
  const [includeProductItems, setIncludeProductItems] = useState(true);


  // ---- Redux state from our new reducer keys ----
  const {
    orders_export,
    loading_orders_export,
    orders_export_error,
  } = useSelector((state) => state.orders);

  // ---- Local date state (default: today to 3 months ago) ----
  const [startDate, setStartDate] = useState(() => getDefaultDates().start);
  const [endDate, setEndDate] = useState(() => getDefaultDates().end);

  // ---- Show/hide customer details ----
  const [includeCustomerDetails, setIncludeCustomerDetails] = useState(false);

  // Shaker Pro orders are merged into the US export by the backend.

  // Derived total
  const totalOrdersExport = Array.isArray(orders_export)
    ? orders_export.length
    : 0;

  // How many columns are visible in the table right now
  // Order Id, Date, Customer Name, Tracking, Total, Line 1, City, State, Zip, Country = 10,
  // plus Email and Phone when the toggle is on. Used for the colSpan of the
  // empty/loading row, which misaligns if this drifts from the header count.
  const visibleColumns = includeCustomerDetails ? 12 : 10;

  // ---- Initial fetch: run once with default dates ----
  useEffect(() => {
    if (startDate && endDate) {
      dispatch(fetchOrdersExport(startDate, endDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // ---- CSV Export ----
  const handleExportCsv = () => {
    if (!orders_export || !orders_export.length) return;

    // If we’re including products, figure out the maximum number
    // of items on any order (so we know how many Product N columns to create)
    const maxItems = includeProductItems
      ? orders_export.reduce((max, order) => {
          const count = Array.isArray(order.items) ? order.items.length : 0;
          return Math.max(max, count);
        }, 0)
      : 0;

    // Build headers dynamically based on PII + product toggle
    const headers = [
      "orderDate",
      "orderId",
      "orderNumber",
      // Fulfilment sits immediately beside the customer name, which is where it is
      // read in practice: "who is this and have we shipped it". It is NOT inside the
      // PII toggle — a fulfilment state is not personal data, so the column is always
      // exported and the toggle only controls the name/email/phone around it.
      // The NAME columns are always exported. The toggle now gates only email and
      // phone.
      //
      // Why: an unticked export already included the full street address, city,
      // state and zip on essentially every row, so gating the name while shipping
      // where the customer lives was never a coherent privacy boundary — it just
      // made the file harder to use. Names are no more identifying than the address
      // already present, and fulfilment work needs a name against an address.
      //
      // firstName/lastName are the STORED columns, not a split of customerName —
      // the backend refuses to guess a surname, so a source that records only a
      // combined name leaves them empty.
      "customerName",
      "firstName",
      "lastName",
      "fulfillmentStatus",
      "tracking",
      ...(includeCustomerDetails ? ["email", "phone"] : []),
      "totalAmount",
      "currency",
      "paymentStatus",
      "line1",
      "city",
      "state",
      "zip",
      "country",
    ];

    if (includeProductItems && maxItems > 0) {
      for (let i = 1; i <= maxItems; i++) {
        headers.push(`Product ${i}`);
        // Color sits between the product name and its quantity because that is the
        // order a picker reads the row: "which product, which colour, how many". The
        // value comes from the same line's variantName (engine order_line.variant_name /
        // Saleor line.variant.name) — see the API side of this change. Products with
        // no colour variant (e.g. Shaker Pro) return an empty string, matching the
        // same "empty means not recorded" convention this file uses for firstName.
        headers.push(`Product ${i} Color`);
        headers.push(`Product ${i} Qty`);
      }
    }

    const escapeCell = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      const escaped = str.replace(/"/g, '""'); // Escape quotes
      return `"${escaped}"`;
    };

    const rows = orders_export.map((order) => {
      const cells = [
        order.orderDate,
        order.orderId,
        order.orderNumber || "",
      ];

      // Passed through verbatim rather than collapsed to a fulfilled/unfulfilled
      // boolean. The backend resolves richer states — "Partially fulfilled" and
      // "Cancelled" — and flattening those would state something false: a partially
      // shipped order is not unfulfilled, and a cancelled one is neither.
      // Absent reads as "Unknown" rather than an empty cell, so a gap is legible.
      const fulfillment = order.fulfillmentStatus || "Unknown";

      // Empty rather than derived: an empty cell reads as "not recorded", while a
      // guessed surname states something false about a customer.
      cells.push(
        order.customerName || "",
        order.firstName || "",
        order.lastName || "",
        fulfillment,
        order.tracking || ""
      );
      if (includeCustomerDetails) {
        cells.push(order.email, order.phone);
      }

      cells.push(
        order.totalAmount,
        order.currency || "",
        order.paymentStatus || "",
        order.line1,
        order.city,
        order.state,
        order.zip,
        order.country
      );

      if (includeProductItems && maxItems > 0) {
        const items = Array.isArray(order.items) ? order.items : [];
        for (let i = 0; i < maxItems; i++) {
          const item = items[i];
          cells.push(item ? item.title : "");
          // variantName carries the line's colour when the product has one. When
          // absent (Shaker Pro, one-variant SKUs, or ShopMy/Shakerpro/Collins
          // attribution rows that never populate items) we push "" rather than a
          // guessed value — an empty cell reads as "not recorded", where
          // "Black" on a colourless product would state something false.
          cells.push(item ? (item.variantName || "") : "");
          cells.push(item ? item.quantity : "");
        }
      }

      return cells.map(escapeCell).join(",");
    });

    const csvContent = [headers.map(escapeCell).join(","), ...rows].join("\r\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const safeStart = startDate || "start";
    const safeEnd = endDate || "end";
    link.download = `orders_export_${safeStart}_to_${safeEnd}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };


  // Format DB "YYYY-MM-DD HH:MM:SS" as local-style text
  // but WITHOUT shifting for browser timezone
  // Format DB timestamp as local-style text
// but WITHOUT timezone conversion.
// Works for:
// - "2025-12-03 07:25:35"
// - "2025-09-04T17:28:15.000Z"
// - "2025-09-04T17:28:15"
const formatDbDateNoTz = (value) => {
  if (!value) return "";

  let s = String(value).trim();

  // Normalise " " vs "T"
  s = s.replace(" ", "T");

  // Strip fractional seconds + optional trailing Z, e.g. ".000" or ".000Z"
  s = s.replace(/\.\d+Z?$/, "");

  // Strip any remaining plain Z or timezone offsets (+hh:mm / -hh:mm)
  s = s.replace(/Z$/, "").replace(/([+-]\d{2}:?\d{2})$/, "");

  // Now "2025-09-04T17:28:15.000Z" -> "2025-09-04T17:28:15"
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(value);

  // Local *format* only; time is treated as naive local wall-clock
  return d.toLocaleString(undefined, {
    year:   "numeric",
    month:  "2-digit",
    day:    "2-digit",
    hour:   "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};



  // ---- Render ----
  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ul className="breadcrumb">
            <h6>
              <li className="breadcrumb-item active">PAGES /</li>
            </h6>
            <h6>
              <li className="breadcrumb-item">&nbsp;ORDERS EXPORT</li>
            </h6>
          </ul>
          <h1 className="page-header mb-0">Orders Export</h1>
          <div className="text-muted small">
            Default range: last 3 months from today. You can adjust dates and rerun.
          </div>
        </div>
      </div>

      <Card className="mb-4">
        {/* Padding wrapper so content doesn't touch the card edge */}
        <div className="p-3 p-md-4">
          {/* Filters + buttons */}
          <div className="row g-3 align-items-end mb-3">
            <div className="col-md-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            

            <div className="col-md-6 d-flex flex-column flex-md-row gap-2 justify-content-md-end align-items-md-end">
              {/* Checkbox for including Product Items*/}
              <div
                className="col-md-4 d-flex align-items-center"
                style={{ alignSelf: "center" }}
              >
                <input
                  type="checkbox"
                  id="includeProductItems"
                  className="me-2"
                  checked={includeProductItems}
                  onChange={(e) => setIncludeProductItems(e.target.checked)}
                />
                <label htmlFor="includeProductItems" className="mb-0">
                  Include Product Items
                </label>
              </div>
              {/* Checkbox for including Customer Details*/}
              <div
                className="col-md-4 d-flex align-items-center"
                style={{ alignSelf: "center" }}
              >
                <input
                  type="checkbox"
                  id="includeCustomerDetails"
                  className="me-2"
                  checked={includeCustomerDetails}
                  onChange={(e) => setIncludeCustomerDetails(e.target.checked)}
                />
                <label htmlFor="includeCustomerDetails" className="mb-0">
                  Include Customer Details
                </label>
              </div>


              <div className="d-flex gap-2 justify-content-md-end">
                <button
                  type="button"
                  className="btn btn-theme"
                  onClick={() => {
                    if (startDate && endDate) {
                      dispatch(fetchOrdersExport(startDate, endDate));
                    }
                  }}
                  disabled={!startDate || !endDate || loading_orders_export}
                >
                  {loading_orders_export ? "Loading…" : "Run"}
                </button>

                <button
                  type="button"
                  className="btn btn-outline-theme"
                  onClick={handleExportCsv}
                  disabled={
                    loading_orders_export ||
                    !orders_export ||
                    !orders_export.length
                  }
                >
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          {orders_export_error && (
            <div className="alert alert-danger py-2 mb-2">
              {orders_export_error}
            </div>
          )}

          <div
            className="mt-4 mb-4 text-muted"
            style={{ fontSize: "1.05rem", letterSpacing: "0.02em", paddingLeft: 5 }}
          >
            <span className="fw-semibold">Total Orders:&nbsp;</span>
            <span className="fw-bold">{totalOrdersExport}</span>
          </div>

          {/* Table */}
          <div className="table-responsive mt-2">
            <table className="table table-hover text-nowrap">
              <thead>
                <tr>
                  <th className="border-top-0 pt-0 pb-2">Order Id</th>
                  <th className="border-top-0 pt-0 pb-2">Date</th>
                  {/* Name is always shown, matching the CSV. The toggle gates only
                      email and phone — the table already displays the full street
                      address regardless, so hiding the name was never a privacy
                      boundary, just an obstacle to reading the page. */}
                  <th className="border-top-0 pt-0 pb-2">Customer Name</th>
                  {includeCustomerDetails && (
                    <>
                      <th className="border-top-0 pt-0 pb-2">Email</th>
                      <th className="border-top-0 pt-0 pb-2">Phone</th>
                    </>
                  )}
                  <th className="border-top-0 pt-0 pb-2">Total Amount</th>
                  <th className="border-top-0 pt-0 pb-2">Tracking</th>
                  <th className="border-top-0 pt-0 pb-2">Address Line 1</th>
                  <th className="border-top-0 pt-0 pb-2">City</th>
                  <th className="border-top-0 pt-0 pb-2">State</th>
                  <th className="border-top-0 pt-0 pb-2">Zip</th>
                  <th className="border-top-0 pt-0 pb-2">Country</th>
                </tr>
              </thead>
              <tbody>
                {loading_orders_export ? (
                  <tr>
                    <td colSpan={visibleColumns} className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : !orders_export || orders_export.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns} className="text-center">
                      No records for selected range
                    </td>
                  </tr>
                ) : (
                  orders_export.map((order, idx) => {

                    return (
                      <tr key={order.orderId || idx}>
                        <td className="align-middle">
                          {order.orderNumber
                            ? `#${order.orderNumber}`
                            : order.orderId
                            ? `#${order.orderId}`
                            : ""}
                        </td>
                        <td className="align-middle">{formatDbDateNoTz(order.orderDate)}</td>

                        <td className="align-middle">
                          {order.customerName || (
                            <span className="text-inverse text-opacity-50">
                              Name not provided
                            </span>
                          )}
                        </td>
                        {includeCustomerDetails && (
                          <>
                            <td className="align-middle">{order.email}</td>
                            <td className="align-middle">{order.phone}</td>
                          </>
                        )}

                        <td className="align-middle">
                          {order.totalAmount != null
                            ? Number(order.totalAmount).toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )
                            : ""}
                        </td>
                        <td className="align-middle">{order.tracking || ""}</td>
                        <td className="align-middle">{order.line1}</td>
                        <td className="align-middle">{order.city}</td>
                        <td className="align-middle">{order.state}</td>
                        <td className="align-middle">{order.zip}</td>
                        <td className="align-middle">{order.country}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
}

<style>
  
</style>

export default OrdersExport;

